/**
 * Live telemetry stream hook for the Map Player plugin.
 *
 * Subscribes to the dashboard SSE feed and accumulates `device_telemetry`
 * frames whose `session_id` matches the active session. Synthesizes a
 * `GpsSummary`-shaped object so the existing map/chart/incident-log
 * components can render without branching for live vs post-session.
 *
 * IMU samples ride the same SSE stream (Phase 2.B.4 — every
 * `device_telemetry` frame carries gps + battery + network + imu); we
 * buffer them too so ImuChart can render live during recording
 * instead of showing "loading IMU…" forever.
 *
 * The hook decimates writes to the React state to ~10 Hz so a chatty
 * box pump (1Hz GPS samples + occasional incident bursts) doesn't
 * trigger excessive map redraws while still keeping the map polyline
 * tight against the video playhead.
 *
 * Once the session ends (`enabled` flips false), the hook keeps the last
 * accumulated samples around so the UI doesn't blink — the parent then
 * fetches the post-session summary and the live data is dropped on
 * unmount.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';
import {
  FleetEvent,
  GpsSummary,
  SessionTelemetryImuSummary,
  SessionTelemetrySample,
} from '../../types';
import { haversineMeters } from './util';

interface LiveSample {
  lat: number;
  lon: number;
  ts_ms: number;
  speed_mps: number;
}

export interface LiveStreamResult {
  /** Whether SSE is currently subscribed (true while session active). */
  active: boolean;
  /** Accumulated GPS samples (newest last). Capped at 7200 (~2h). */
  samples: LiveSample[];
  /** Synthetic GpsSummary the existing components understand. Null until first fix lands. */
  liveSummary: GpsSummary | null;
  /** Latest fix UTC, suitable for `currentUtcMs` driving the playhead. */
  latestUtcMs: number | null;
  /** Accumulated IMU samples (newest last). Same shape as the
   *  post-session telemetry rows so ImuChart consumes them without a
   *  branch. Capped at 7200 (~2h at 1 Hz summary cadence). */
  liveImuSamples: SessionTelemetrySample[];
}

const MAX_SAMPLES = 7200; // ≈ 2h at 1Hz

// H1 — flush coalescing window. Was 500 ms; lowered to 100 ms so the
// map polyline catches up to the video playhead. At 1 Hz GPS, this
// roughly means one flush per fix arriving (with bursts coalescing).
// Cost: more React re-renders, but the map/chart components are
// already memoized on their data array.
const FLUSH_INTERVAL_MS = 100;

export function useLiveGpsStream(
  sessionId: string | undefined,
  enabled: boolean,
): LiveStreamResult {
  // Underlying buffers kept in refs so SSE callbacks can append cheaply
  // without retriggering the whole component tree every frame.
  const bufferRef = useRef<LiveSample[]>([]);
  const imuBufferRef = useRef<SessionTelemetrySample[]>([]);
  const [tick, setTick] = useState<number>(0);
  const flushTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled || !sessionId) {
      // When the session ends, leave buffers intact so a subsequent
      // re-render still sees the trail until the parent swaps in the
      // final summary. Skip resubscribing.
      return;
    }
    // Reset buffers on session change to avoid mixing tracks.
    bufferRef.current = [];
    imuBufferRef.current = [];
    setTick((n) => n + 1);

    const scheduleFlush = () => {
      if (flushTimerRef.current != null) return;
      flushTimerRef.current = window.setTimeout(() => {
        flushTimerRef.current = null;
        setTick((n) => n + 1);
      }, FLUSH_INTERVAL_MS);
    };

    const onEvent = (evt: FleetEvent) => {
      if (evt.type !== 'device_telemetry') return;
      if (evt.session_id !== sessionId) return;

      // GPS sample — what the polyline + speed chart consume.
      const g = evt.gps;
      if (g && g.lat != null && g.lon != null) {
        const tsMs = g.fix_ts_ms ?? (evt.ts != null ? evt.ts * 1000 : Date.now());
        const buf = bufferRef.current;
        // Reject out-of-order or duplicate timestamps so the polyline
        // stays monotonic and SpeedChart's x-axis doesn't snake back.
        if (buf.length === 0 || tsMs > buf[buf.length - 1].ts_ms) {
          buf.push({
            lat: g.lat,
            lon: g.lon,
            ts_ms: tsMs,
            speed_mps: g.speed_mps ?? 0,
          });
          if (buf.length > MAX_SAMPLES) {
            buf.splice(0, buf.length - MAX_SAMPLES);
          }
          scheduleFlush();
        }
      }

      // IMU sample — what ImuChart consumes. C4 — phone's V2 native
      // bridge emits a 1 s window summary (accel_mag_*, jerk_peak,
      // gyro_mag_peak) at 1 Hz GPS cadence. Pre-this-fix ImuChart did
      // a single REST fetch on mount and never refreshed, so live
      // recordings showed "loading IMU…" forever.
      if (evt.imu) {
        // The SSE event's `imu` field can carry either:
        //   - V2 native ImuBridge summary (SessionTelemetryImuSummary shape)
        //   - legacy V1 web-orientation (alpha/beta/gamma)
        // We accept either via the union type below. ImuChart filters
        // for the V2 shape since gamma/beta/alpha aren't plotted.
        const imuPayload = evt.imu as
          | SessionTelemetryImuSummary
          | { alpha_deg?: number | null }
          | Record<string, unknown>;
        const tsMs = evt.ts != null ? evt.ts * 1000 : Date.now();
        const imuBuf = imuBufferRef.current;
        if (imuBuf.length === 0 || tsMs > imuBuf[imuBuf.length - 1].ts_ms) {
          imuBuf.push({
            ts_ms: tsMs,
            // H6 — pass through the phone's CLOCK_BOOTTIME ns so ImuChart
            // can reconcile via the session anchor at render time.
            mono_ns: evt.mono_ns ?? null,
            session_id: sessionId,
            gps: null,
            battery: null,
            network: null,
            imu: imuPayload as SessionTelemetryImuSummary,
          });
          if (imuBuf.length > MAX_SAMPLES) {
            imuBuf.splice(0, imuBuf.length - MAX_SAMPLES);
          }
          scheduleFlush();
        }
      }
    };

    const unsub = api.subscribeFleetEvents(onEvent);
    return () => {
      unsub();
      if (flushTimerRef.current != null) {
        window.clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
    };
  }, [enabled, sessionId]);

  // Materialise the synthetic summary off the buffer. `tick` participates
  // so React re-runs the memo when SSE flushes happen.
  const liveSummary = useMemo<GpsSummary | null>(() => {
    const buf = bufferRef.current;
    if (buf.length === 0) return null;
    const track: GpsSummary['track'] = buf.map((s) => [
      s.lat, s.lon, s.ts_ms, s.speed_mps,
    ]);
    let dist = 0;
    let maxSpeed = 0;
    let speedSum = 0;
    for (let i = 0; i < buf.length; i += 1) {
      const s = buf[i];
      if (s.speed_mps > maxSpeed) maxSpeed = s.speed_mps;
      speedSum += s.speed_mps;
      if (i > 0) {
        dist += haversineMeters(
          buf[i - 1].lat, buf[i - 1].lon, s.lat, s.lon,
        );
      }
    }
    return {
      samples: buf.length,
      valid: buf.length,
      track,
      stops: [],
      incidents: [],
      total_distance_km: dist / 1000,
      max_speed_mps: maxSpeed,
      avg_speed_mps: buf.length > 0 ? speedSum / buf.length : 0,
      first_utc_ms: buf[0]?.ts_ms ?? null,
      last_utc_ms: buf[buf.length - 1]?.ts_ms ?? null,
      generated_at_ms: Date.now(),
    };
    // `tick` deliberately included so the memo busts on every flush.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);

  // IMU samples — same `tick` dependency so consumers re-render when
  // new IMU data arrives via SSE.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const liveImuSamples = useMemo(() => [...imuBufferRef.current], [tick]);

  const latestUtcMs = bufferRef.current.length > 0
    ? bufferRef.current[bufferRef.current.length - 1].ts_ms
    : null;

  return {
    active: enabled && !!sessionId,
    samples: bufferRef.current,
    liveSummary,
    latestUtcMs,
    liveImuSamples,
  };
}
